import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/product/ProductCard";

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [facets, setFacets] = useState({ categories: [], designers: [], sizes: [], conditions: [] });
    const [loading, setLoading] = useState(true);

    const sort = params.get("sort") || "newest";
    const category = params.get("category") || "";
    const designer = params.get("designer") || "";
    const size = params.get("size") || "";
    const condition = params.get("condition") || "";
    const edit = params.get("edit") || "";

    const query = useMemo(() => {
        const p = new URLSearchParams();
        if (category) p.set("category", category);
        if (designer) p.set("designer", designer);
        if (size) p.set("size", size);
        if (condition) p.set("condition", condition);
        if (edit) p.set("edit", edit);
        p.set("sort", sort);
        return p.toString();
    }, [category, designer, size, condition, edit, sort]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [p, f] = await Promise.all([
                    api.get(`/products?${query}`),
                    api.get("/products/facets"),
                ]);
                setItems(p.data.items);
                setTotal(p.data.total);
                setFacets(f.data);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, [query]);

    const setFacet = (key, value) => {
        const next = new URLSearchParams(params);
        if (next.get(key) === value) next.delete(key);
        else next.set(key, value);
        setParams(next);
    };

    const clearAll = () => {
        const next = new URLSearchParams();
        if (sort !== "newest") next.set("sort", sort);
        setParams(next);
    };

    return (
        <div className="shop" data-testid="shop-page">
            <div className="shop-head">
                <div className="eyebrow" style={{ marginBottom: 12 }}>
                    {edit ? "The Edit" : "Shop"}
                </div>
                <h1 className="h-display">
                    {edit ? edit.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "The Full Edit"}
                </h1>
            </div>

            <div className="shop-body">
                <aside className="shop-sidebar" data-testid="shop-sidebar">
                    <div className="filter-group">
                        <h4>Category</h4>
                        {facets.categories.map((c) => (
                            <label key={c}>
                                <input
                                    type="checkbox"
                                    checked={category === c}
                                    onChange={() => setFacet("category", c)}
                                    data-testid={`filter-category-${c}`}
                                />
                                {c}
                            </label>
                        ))}
                    </div>
                    <div className="filter-group">
                        <h4>Designer</h4>
                        {facets.designers.slice(0, 10).map((d) => (
                            <label key={d}>
                                <input
                                    type="checkbox"
                                    checked={designer === d}
                                    onChange={() => setFacet("designer", d)}
                                    data-testid={`filter-designer-${d.replace(/\s+/g, "-")}`}
                                />
                                {d}
                            </label>
                        ))}
                    </div>
                    <div className="filter-group">
                        <h4>Size</h4>
                        {facets.sizes.map((s) => (
                            <label key={s}>
                                <input
                                    type="checkbox"
                                    checked={size === s}
                                    onChange={() => setFacet("size", s)}
                                />
                                {s}
                            </label>
                        ))}
                    </div>
                    <div className="filter-group">
                        <h4>Condition</h4>
                        {facets.conditions.map((c) => (
                            <label key={c}>
                                <input
                                    type="checkbox"
                                    checked={condition === c}
                                    onChange={() => setFacet("condition", c)}
                                />
                                {c}
                            </label>
                        ))}
                    </div>
                    {(category || designer || size || condition || edit) && (
                        <button className="link-underline" onClick={clearAll} data-testid="clear-filters">
                            Clear filters
                        </button>
                    )}
                </aside>

                <div>
                    <div className="shop-toolbar">
                        <div data-testid="shop-count">{total} piece{total === 1 ? "" : "s"}</div>
                        <label>
                            <span className="sr-only">Sort by</span>
                            <select
                                value={sort}
                                onChange={(e) => {
                                    const next = new URLSearchParams(params);
                                    next.set("sort", e.target.value);
                                    setParams(next);
                                }}
                                data-testid="shop-sort"
                            >
                                <option value="newest">Newest</option>
                                <option value="price_asc">Price: low → high</option>
                                <option value="price_desc">Price: high → low</option>
                                <option value="designer">Designer A–Z</option>
                            </select>
                        </label>
                    </div>

                    {loading ? (
                        <div className="loading">Loading pieces…</div>
                    ) : items.length === 0 ? (
                        <div className="empty-state">
                            No pieces match those filters yet.
                            <div style={{ marginTop: 16 }}>
                                <button className="link-underline" onClick={clearAll}>
                                    Clear filters
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="product-grid" data-testid="shop-product-grid">
                            {items.map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
